import { defineStore } from "pinia";
import { post, get, del } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { getInfoPromise, saveCurrentDeviceId } from "@binance/fingerprint";
import { setCookie } from "../utils/cookies";
import {
  postMedicalBoothsUsersValidation,
  postMedicalBoothsUsersAffiliation,
  getServiceLink,
  endConsultation,
  postEvento,
} from "../utils/interfaces";

function buildQueryString(params: any) {
  return Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join("&");
}

export const useGeneralStore = defineStore("general", {
  state: () => ({
    booth: {} as any,
  }),
  getters: {
    getBooth: (state) => state.booth,
  },
  actions: {
    setBooth(data: any) {
      this.booth = data;
    },
    async getSession() {
      const session = await fetchAuthSession();
      return session;
    },
    async generateFingerprint(callback: () => void) {
      try {
        const session = await this.getSession();
        let userId;
        if (session.userSub) {
          userId = session.tokens?.idToken?.payload?.email as string;
          saveCurrentDeviceId(userId, session.userSub);
        }
        const deviceFingerprint = await getInfoPromise(userId);
        setCookie("fingerprint", deviceFingerprint);
        if (callback) callback();
      } catch (err) {
        console.error("Error generando fingerprint:", err);
      }
    },
    async postMedicalBoothsValidation(data: postMedicalBoothsUsersValidation) {
      try {
        const tokens = await fetchAuthSession();
        const token = tokens.tokens?.idToken?.toString();
        const res = await (
          await post({
            apiName: "vivawell",
            path: `/cabinas-medicas/valida`,
            options: {
              headers: { Authorization: token as string },
              body: data as any,
            },
          }).response
        ).body.json();
        return res;
      } catch (err: any) {
        console.log(err);
        return {
          statusCode: err._response?.statusCode,
          message: JSON.parse(err._response?.body),
        };
      }
    },
    async postMedicalBoothsAffiliation(
      data: postMedicalBoothsUsersAffiliation,
      query: { onlySearch?: boolean }
    ) {
      try {
        const tokens = await fetchAuthSession();
        const token = tokens.tokens?.idToken?.toString();
        const res = await (
          await post({
            apiName: "vivawell",
            path: `/cabinas-medicas/persona?${buildQueryString(query)}`,
            options: {
              headers: { Authorization: token as string },
              body: data as any,
            },
          }).response
        ).body.json();
        return res;
      } catch (err: any) {
        console.log(err);
        return {
          statusCode: err._response?.statusCode,
          message: JSON.parse(err._response?.body),
        };
      }
    },
    async getServiceLink(queryStringParameters: getServiceLink) {
      try {
        const tokens = await fetchAuthSession();
        const token = tokens.tokens?.idToken?.toString();
        const query = [];
        for (const key in queryStringParameters) {
          query.push(
            `${key}=${queryStringParameters[key as keyof getServiceLink]}`
          );
        }
        const path = `/cabinas-medicas/persona/servicio?${query.join("&")}`;
        const res = await (
          await get({
            apiName: "vivawell",
            path,
            options: { headers: { Authorization: token as string } },
          }).response
        ).body.json();
        return res;
      } catch (err: any) {
        console.log(err);
        return {
          statusCode: err._response?.statusCode,
          message: JSON.parse(err._response?.body),
        };
      }
    },
    async sendResults(idCardioRisk: string, email: string) {
      try {
        const tokens = await fetchAuthSession();
        const token = tokens.tokens?.idToken?.toString();
        const res = await (
          await post({
            apiName: "vivawell",
            path: `/cabinas-medicas/enviar-resultado/${idCardioRisk}`,
            options: {
              headers: { Authorization: token as string },
              body: { email },
            },
          }).response
        ).body.json();
        return res;
      } catch (err: any) {
        console.log(err);
        return {
          statusCode: err._response?.statusCode,
          message: JSON.parse(err._response?.body),
        };
      }
    },
    async endConsultation(data: endConsultation) {
      try {
        const tokens = await fetchAuthSession();
        const token = tokens.tokens?.idToken?.toString();
        const res = await (
          await post({
            apiName: "vivawell",
            path: "/cabinas-medicas/finalizar-consulta",
            options: {
              headers: { Authorization: token as string },
              body: data as any,
            },
          }).response
        ).body.json();
        return res;
      } catch (err: any) {
        console.log(err);
        return {
          statusCode: err._response?.statusCode,
          message: JSON.parse(err._response?.body),
        };
      }
    },
    async deleteBoothSession(idSesion: string) {
      try {
        const tokens = await fetchAuthSession();
        const token = tokens.tokens?.idToken?.toString();
        const res = await del({
          apiName: "vivawell",
          path: `/cabinas-medicas/usuario/sesion/${idSesion}`,
          options: {
            headers: { Authorization: token as string },
          },
        }).response;
        return res;
      } catch (err: any) {
        console.log(err);
        return {
          statusCode: err._response?.statusCode,
          message: JSON.parse(err._response?.body),
        };
      }
    },
    async postEvent(data: postEvento) {
      try {
        const tokens = await fetchAuthSession();
        const token = tokens.tokens?.idToken?.toString();
        const res = await (
          await post({
            apiName: "vivawell",
            path: "/cabinas-medicas/evento",
            options: {
              headers: { Authorization: token as string },
              body: data as any,
            },
          }).response
        ).body.json();
        return res;
      } catch (err: any) {
        console.log(err);
        return {
          statusCode: err._response?.statusCode,
          message: JSON.parse(err._response?.body),
        };
      }
    },
    async getConsultas(idPersona: string) {
      try {
        const tokens = await fetchAuthSession();
        const token = tokens.tokens?.idToken?.toString();
        const res = await (
          await get({
            apiName: "vivawell",
            path: `/consultas?idPersona=${idPersona}`,
            options: { headers: { Authorization: token as string } },
          }).response
        ).body.json();
        return res;
      } catch (err: any) {
        console.log(err);
        return {
          statusCode: err._response?.statusCode,
          message: JSON.parse(err._response?.body),
        };
      }
    },

    async addPhoneToPerson(idPersona: string, phone: any) {
      const tokens = await fetchAuthSession();
      const token = tokens.tokens?.idToken?.toString();
      const requestInfo = {
        headers: { Authorization: token },
        body: phone,
      };
      const res = await (
        await post({
          apiName: "vivawell",
          path: `/personas/${idPersona}/telefonos?grupoRegistro=ABM_CABINAS&idGrupoRegistro=${idPersona}`,
          options: requestInfo as any,
        }).response
      ).body.json();
      console.log("respuesta de agregar telefono -->", res);

      return res;
    },
    async addEmailToPerson(idPersona: string, email: any) {
      const tokens = await fetchAuthSession();
      const token = tokens.tokens?.idToken?.toString();
      const requestInfo = {
        headers: { Authorization: token },
        body: email,
      };
      const res = await (
        await post({
          apiName: "vivawell",
          path: `/personas/${idPersona}/emails?grupoRegistro=ABM_CABINAS&idGrupoRegistro=${idPersona}`,
          options: requestInfo as any,
        }).response
      ).body.json();
      return res;
    },
  },
});
